import { Route, Routes, Navigate } from 'react-router'
import { ErrorBoundary } from 'react-error-boundary'
import { Loader, Stack } from '@mantine/core'
import { Suspense, lazy } from 'react'
import { useActiveCompetitions } from './navigation/api'
import { ErrorFallback, handleError } from './common/error-handler'
import LoginPage from './auth/LoginPage'
import PasswordResetPage from './auth/PasswordResetPage'
import ForgotPasswordPage from './auth/ForgotPasswordPage'
import Layout from './common/Layout'
import News from './news/News'
import Arena from './arena/Arena'
import NewsDetails from './news/NewsDetails'
import MetricsDemo from './widgets/Demo'
import { DeviceApprovalPage } from './device-link'
const DeviceDashboard = lazy(() => import('./device-link/DeviceDashboard'))
import Teams from './teams/Teams'
import Players from './players/Players'
import Matches from './matches/Matches'
import TeamDetails from './teams/TeamDetails'
import Player from './players/Player'
import Ranks from './ranks/Ranks'
import Performance from './performance/Performance'
import MatchSummary from './matches/MatchSummary'
import Settings from './settings/Settings'
import MatchHistory from './matches/MatchHistory'
import MatchDetailsLayout from './matches/MatchDetailsLayout'
import TeamOverview from './teams/components/TeamOverview'
import TeamMatches from './teams/components/TeamMatches'
import TeamPlayers from './teams/components/TeamPlayers'
import PerformanceSummary from './performance/components/PerformanceAnalysis'
import PerformanceKpiRank from './performance/components/PerformanceKpiRank'
import ClairChat from './clair/ClairChat'
import ContentPage from './content_pages/ContentPage'
import WatchMatch from './matches/WatchMatch'
import Sitemap from './sitemap'
import Home from './home/Home'
import Dashboard, { DashboardPreviewByIdPage } from './dashboard/Dashboard'
import Compare from './compare/Compare'
import Learning from './learning/Learning'
import Races from './races/Races'
import Course from './learning/Course'
import QuizAttemptLayout from './learning/QuizAttemptLayout'
import QuizConsent from './learning/QuizConsent'
import QuestionGroup from './learning/QuestionGroup'
import QuizResult from './learning/QuizResult'
import RaceDetailsLayout from './races/RaceDetailsLayout'
import RaceSummary from './races/RaceSummary'
import RaceHighlights from './races/RaceHighlights'
import { useCompetitionId } from './common/hooks'
import LeagueLadderLayout from './ladder/LeagueLadderLayout'
import TeamRaces from './teams/components/TeamRaces'
import LoxoKpis from './loxo/LoxoKpis'
import './admin/admin.css'

// Admin pages - lazy loaded
const AdminPages = lazy(() => import('./admin/pages/AdminPages'))

function HomeRedirect() {
  const competition_id = useCompetitionId()
  const { data } = useActiveCompetitions()

  // Wait for competition to be auto-selected
  if (!competition_id && data?.competitions?.length) {
    return (
      <Stack w="full" h="100%" justify="center" align="center">
        <Loader color="#0B6B7B" />
      </Stack>
    )
  }

  if (competition_id) {
    return <Navigate to={`/competitions/${competition_id}/home`} replace />
  }

  // Fallback to news if no competitions available
  return <Navigate to="/news" replace />
}

export function AppRoutes() {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/news" element={<News />} />
          <Route path="/news/:newsId" element={<NewsDetails />} />
          <Route path="/page/:slug" element={<ContentPage />} />
          <Route path="/competitions/:competitionId/home" element={<Home />} />
          <Route
            path="/competitions/:competitionId/arena"
            element={<Arena />}
          />
          <Route
            path="/competitions/:competitionId/matches"
            element={<Matches />}
          />
          <Route
            path="/competitions/:competitionId/matches/:matchId"
            element={<MatchDetailsLayout />}
          >
            <Route path="summary" element={<MatchSummary />} />
            <Route path="watch" element={<WatchMatch />} />
            <Route
              path=":team1_id/history/:team2_id"
              element={<MatchHistory />}
            />
          </Route>
          <Route
            path="/competitions/:competitionId/races"
            element={<Races />}
          />
          <Route
            path="/competitions/:competitionId/races/:raceId"
            element={<RaceDetailsLayout />}
          >
            <Route path="summary" element={<RaceSummary />} />
            <Route path="highlights" element={<RaceHighlights />} />
          </Route>
          <Route
            path="/competitions/:competitionId/teams"
            element={<Teams />}
          />
          <Route
            path="/competitions/:competitionId/teams/:teamId"
            element={<TeamDetails />}
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<TeamOverview />} />
            <Route path="matches" element={<TeamMatches />} />
            <Route path="races" element={<TeamRaces />} />
            <Route path="players" element={<TeamPlayers />} />
          </Route>
          <Route
            path="/competitions/:competitionId/players"
            element={<Players />}
          />
          <Route
            path="/competitions/:competitionId/players/:playerId"
            element={<Player />}
          />
          <Route
            path="/competitions/:competitionId/ladder"
            element={<LeagueLadderLayout />}
          />
          <Route path="/competitions/:competitionId/ranks">
            <Route index element={<Navigate to="teams" replace />} />
            <Route path=":tab" element={<Ranks />} />
          </Route>
          <Route
            path="/competitions/:competitionId/performance"
            element={<Performance />}
          >
            <Route index element={<Navigate to="analysis" replace />} />
            <Route path="analysis" element={<PerformanceSummary />} />
            <Route path="kpi">
              <Route index element={<Navigate to="teams" replace />} />
              <Route path=":subject" element={<PerformanceKpiRank />} />
            </Route>
          </Route>
          <Route
            path="/competitions/:competitionId/compare"
            element={<Compare />}
          />
          <Route path="/learning" element={<Learning />} />

          <Route path="/learning/course/:courseId" element={<Course />} />

          <Route
            path="/learning/course/:courseId/result"
            element={<QuizResult />}
          />

          <Route
            path="/learning/course/:courseId/consent"
            element={<QuizConsent />}
          />

          <Route
            path="/learning/course/:courseId/quiz/:attemptId"
            element={<QuizAttemptLayout />}
          >
            <Route
              path="question-group/:questionGroupId"
              element={<QuestionGroup />}
            />
          </Route>

          <Route path="/explore/loxo" element={<LoxoKpis />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/clair" element={<ClairChat />} />
          <Route path="/sitemap" element={<Sitemap />} />

          {/* Deprecated routes */}
          <Route path="/metrics" element={<MetricsDemo />} />
          <Route path="/device" element={<DeviceApprovalPage />} />

          {/* Admin routes */}
          <Route
            path="/a/*"
            element={
              <Suspense
                fallback={
                  <Stack align="center" justify="center" h="400px">
                    <Loader size="lg" />
                  </Stack>
                }
              >
                <AdminPages />
              </Suspense>
            }
          />
        </Route>
        <Route
          path="/tv"
          element={
            <Suspense fallback={null}>
              <DeviceDashboard />
            </Suspense>
          }
        />
        <Route
          path="/competitions/:competitionId/dashboard"
          element={<Dashboard />}
        />
        <Route
          path="/dashboards/:dashboardId/preview"
          element={<DashboardPreviewByIdPage />}
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/password-reset" element={<PasswordResetPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      </Routes>
    </ErrorBoundary>
  )
}
